import { apiClient } from './client'
import type { ApiResponse, PageResponse } from '@/types/api'

// ── Types ────────────────────────────────────────────────────────────

export interface GradeListItem {
  quetureId: string
  title: string | null
  grade: string | null
  hasQnIntro: boolean
  hasQnOutro: boolean
  hasQueboardConfig: boolean
  queboardProblemCount: number
  hasBuildup: boolean
  buildupStepCount: number
  hasEssentialCheck: boolean
  essentialCheckCount: number
}

export interface GradeFilterParams {
  hasGrade?: boolean
  hasToon?: boolean
  hasQueboard?: boolean
  hasBuildup?: boolean
  hasEssentialCheck?: boolean
}

// ── Buildup Types ───────────────────────────────────────────────────

export interface BuildupItem {
  number: number
  title: string | null
  problem: string | null
  options: string | null
  answer: string | null
  desc: string | null
  answerCriteria: string | null
  teacherCheck: string | null
  probImageUrls: string[]
  descImageUrls: string[]
  probPrintImageUrls: string[]
  descPrintImageUrls: string[]
}

export interface EssentialCheckItem {
  number: number
  answer: string | null
  desc: string | null
}

export interface BuildupStep {
  step: string
  title: string
  items: BuildupItem[]
}

// ── QueBoard Types ──────────────────────────────────────────────────

export interface QueBoardProblem {
  left: number
  right: number
}

export interface QueBoardFeatures {
  tie: boolean
  teleport5: boolean
  teleport10: boolean
  bank: boolean
  indicator: boolean
  customInput: boolean
}

export interface QueBoardTutorial {
  enabled: boolean
  modules: string[]
  autoStart: boolean
}

export type QueBoardCharacter = 'queda' | 'chaeli' | 'ddami' | 'random'

export interface QueBoardConfigEntry {
  problem: QueBoardProblem
  features: QueBoardFeatures
  tutorial: QueBoardTutorial
  character: QueBoardCharacter
}

export interface GradePreviewRow {
  quetureId: string
  grade: string
  status: 'new' | 'update' | 'unchanged' | 'error'
  previousGrade?: string | null
  errorMessage?: string | null
}

export interface GradePreviewResult {
  totalRows: number
  newCount: number
  updateCount: number
  unchangedCount: number
  errorCount: number
  rows: GradePreviewRow[]
}

export interface GradeBulkSaveResult {
  savedCount: number
}

// ── Buildup Bulk Upload Types ──────────────────────────────────────

export interface BuildupPreviewRow {
  quetureId: string
  stepCount: number
  itemCount: number
  status: 'new' | 'update' | 'error'
  errorMessage?: string | null
  steps: BuildupStep[]
  essentialCheck?: EssentialCheckItem[] | null
}

export interface BuildupPreviewResult {
  totalQuetureCount: number
  newCount: number
  updateCount: number
  skippedRowCount: number
  errorCount: number
  rows: BuildupPreviewRow[]
}

export interface BuildupBulkSaveResult {
  savedCount: number
}

// ── Buildup Image Bulk Types ─────────────────────────────────────

export interface ImagePreviewCandidate {
  quetureId: string
  quetureName: string
  hasBuildupData: boolean
}

export interface ImagePreviewMatchedFile {
  fileName: string
  step: number
  itemNumber: number
  location: 'instruction' | 'explanation' | 'instruction_print' | 'explanation_print'
  order: number
}

export interface ImagePreviewMatchGroup {
  code: string
  candidates: ImagePreviewCandidate[]
  files: ImagePreviewMatchedFile[]
}

export interface ImagePreviewError {
  fileName: string
  reason: 'INVALID_FORMAT' | 'NO_MATCH' | 'NO_BUILDUP'
}

export interface ImagePreviewResult {
  matched: ImagePreviewMatchGroup[]
  errors: ImagePreviewError[]
}

export interface ImageBulkSaveEntry {
  quetureId: string
  step: number
  itemNumber: number
  location: 'instruction' | 'explanation' | 'instruction_print' | 'explanation_print'
  imageUrls: string[]
}

export interface ImageBulkSaveResult {
  savedCount: number
}

// ── API ──────────────────────────────────────────────────────────────

export const quetureContentApi = {
  listGrades: async (page = 0, size = 20, search?: string, filters?: GradeFilterParams): Promise<PageResponse<GradeListItem>> => {
    const params: Record<string, string | number | boolean> = { page, size }
    if (search) params.search = search
    if (filters?.hasGrade) params.hasGrade = true
    if (filters?.hasToon) params.hasToon = true
    if (filters?.hasQueboard) params.hasQueboard = true
    if (filters?.hasBuildup) params.hasBuildup = true
    if (filters?.hasEssentialCheck) params.hasEssentialCheck = true
    const res = await apiClient.get<ApiResponse<PageResponse<GradeListItem>>>('/admin/queture-contents/grade', { params })
    return res.data.data!
  },

  previewCsv: async (file: File): Promise<GradePreviewResult> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await apiClient.post<ApiResponse<GradePreviewResult>>(
      '/admin/queture-contents/grade/preview',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return res.data.data!
  },

  bulkSave: async (rows: Array<{ quetureId: string; grade: string }>): Promise<GradeBulkSaveResult> => {
    const res = await apiClient.post<ApiResponse<GradeBulkSaveResult>>(
      '/admin/queture-contents/grade/bulk-save',
      rows,
      { timeout: 60000 }
    )
    return res.data.data!
  },

  updateGrade: async (quetureId: string, grade: string): Promise<GradeListItem> => {
    const res = await apiClient.put<ApiResponse<GradeListItem>>(
      `/admin/queture-contents/grade/${quetureId}`,
      { grade }
    )
    return res.data.data!
  },

  deleteGrade: async (quetureId: string): Promise<void> => {
    await apiClient.delete(`/admin/queture-contents/grade/${quetureId}`)
  },

  downloadSampleCsv: async () => {
    const res = await apiClient.get('/admin/queture-contents/grade/sample-csv', {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = 'queture-grade-sample.csv'
    a.click()
    URL.revokeObjectURL(url)
  },

  // ── QueBoard Config ─────────────────────────────────────────────

  getQueboardConfig: async (quetureId: string): Promise<QueBoardConfigEntry[]> => {
    const res = await apiClient.get<ApiResponse<QueBoardConfigEntry[]>>(
      `/admin/queture-contents/queboard/${quetureId}`
    )
    return res.data.data ?? []
  },

  saveQueboardConfig: async (quetureId: string, config: QueBoardConfigEntry[]): Promise<QueBoardConfigEntry[]> => {
    const res = await apiClient.put<ApiResponse<QueBoardConfigEntry[]>>(
      `/admin/queture-contents/queboard/${quetureId}`,
      config
    )
    return res.data.data ?? []
  },

  deleteQueboardConfig: async (quetureId: string): Promise<void> => {
    await apiClient.delete(`/admin/queture-contents/queboard/${quetureId}`)
  },

  // ── Buildup ─────────────────────────────────────────────────────

  getBuildup: async (quetureId: string): Promise<BuildupStep[]> => {
    const res = await apiClient.get<ApiResponse<BuildupStep[]>>(
      `/admin/queture-contents/buildup/${quetureId}`
    )
    return res.data.data ?? []
  },

  saveBuildup: async (quetureId: string, buildup: BuildupStep[]): Promise<BuildupStep[]> => {
    const res = await apiClient.put<ApiResponse<BuildupStep[]>>(
      `/admin/queture-contents/buildup/${quetureId}`,
      buildup
    )
    return res.data.data ?? []
  },

  deleteBuildup: async (quetureId: string): Promise<void> => {
    await apiClient.delete(`/admin/queture-contents/buildup/${quetureId}`)
  },

  // ── Essential Check ─────────────────────────────────────────────

  getEssentialCheck: async (quetureId: string): Promise<EssentialCheckItem[]> => {
    const res = await apiClient.get<ApiResponse<EssentialCheckItem[]>>(
      `/admin/queture-contents/essential-check/${quetureId}`
    )
    return res.data.data ?? []
  },

  saveEssentialCheck: async (quetureId: string, items: EssentialCheckItem[]): Promise<EssentialCheckItem[]> => {
    const res = await apiClient.put<ApiResponse<EssentialCheckItem[]>>(
      `/admin/queture-contents/essential-check/${quetureId}`,
      { items }
    )
    return res.data.data ?? []
  },

  deleteEssentialCheck: async (quetureId: string): Promise<void> => {
    await apiClient.delete(`/admin/queture-contents/essential-check/${quetureId}`)
  },

  // ── Buildup Bulk Upload ─────────────────────────────────────────

  previewBuildupCsv: async (file: File): Promise<BuildupPreviewResult> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await apiClient.post<ApiResponse<BuildupPreviewResult>>(
      '/admin/queture-contents/buildup/preview',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
    )
    return res.data.data!
  },

  bulkSaveBuildup: async (rows: Array<{ quetureId: string; steps: BuildupStep[]; essentialCheck?: EssentialCheckItem[] | null }>): Promise<BuildupBulkSaveResult> => {
    const res = await apiClient.post<ApiResponse<BuildupBulkSaveResult>>(
      '/admin/queture-contents/buildup/bulk-save',
      rows,
      { timeout: 60000 }
    )
    return res.data.data!
  },

  downloadBuildupSampleCsv: async () => {
    const res = await apiClient.get('/admin/queture-contents/buildup/sample-csv', {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = 'queture-buildup-sample.csv'
    a.click()
    URL.revokeObjectURL(url)
  },

  // ── Buildup Image Bulk Upload ──────────────────────────────────────

  previewBuildupImages: async (fileNames: string[]): Promise<ImagePreviewResult> => {
    const res = await apiClient.post<ApiResponse<ImagePreviewResult>>(
      '/admin/queture-contents/buildup/image-preview',
      { fileNames }
    )
    return res.data.data!
  },

  bulkSaveBuildupImages: async (entries: ImageBulkSaveEntry[]): Promise<ImageBulkSaveResult> => {
    const res = await apiClient.post<ApiResponse<ImageBulkSaveResult>>(
      '/admin/queture-contents/buildup/image-bulk-save',
      entries,
      { timeout: 120000 }
    )
    return res.data.data!
  },
}
